import logo from "@/assets/favicon_io/android-chrome-192x192.png";
import type { FormResponsesPrintCell } from "@/modules/forms/domain/lib/build-form-responses-print-data";
import { config } from "@/shared/lib/config";
import { formatDateTime } from "@/shared/lib/date-time";
import {
  escapeHtmlForPrintUnsafeContext,
  FORM_RESPONSES_PRINT_STYLES_A4,
} from "@/shared/lib/form-responses-print-a4";

import type { FormResponsesPrintDataDTO } from "../api/types";

function renderPrintCellHtml(cell: FormResponsesPrintCell): string {
  if (cell.kind === "file") {
    const href = escapeHtmlForPrintUnsafeContext(cell.url);
    const label = escapeHtmlForPrintUnsafeContext(cell.label);
    return `<a href="${href}" class="frp-file-link">${label}</a>`;
  }
  return escapeHtmlForPrintUnsafeContext(cell.value);
}

async function loadLogoDataUrl(): Promise<string | undefined> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(undefined);
        return;
      }
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(undefined);
    img.src = logo;
  });
}

/** ເປີດເຄື່ອງພິມ A4 ຄືກັກບິນ ຮ້າງ Save as PDF ໄດ້ ຈາກ ປ່ອງ ພິມ. */
export async function printFormResponsesA4(
  data: FormResponsesPrintDataDTO,
): Promise<void> {
  const logoDataUrl = await loadLogoDataUrl();
  const logoBlock =
    logoDataUrl !== undefined
      ? `<img src="${logoDataUrl}" alt="" width="32" height="32" style="display:block;margin:0 auto 6px;object-fit:contain;" />`
      : "";

  const brandRaw = config.brandName ?? "";
  const brand = escapeHtmlForPrintUnsafeContext(brandRaw);
  const title = escapeHtmlForPrintUnsafeContext(data.formTitle);
  const genAtRaw = formatDateTime(data.generatedAt);
  const genAt = escapeHtmlForPrintUnsafeContext(genAtRaw);
  const filteredNote = data.filtered
    ? ' · <span style="font-weight:600;">ກອງຜົນແລ້ວ</span>'
    : "";

  const colCount = 1 + data.columns.length;

  const theadCells = `
    <tr>
      <th style="width:11%;">ເວລາສົ່ງ</th>
      ${data.columns
        .map((c) => `<th>${escapeHtmlForPrintUnsafeContext(c.label)}</th>`)
        .join("")}
    </tr>`;

  const tbody =
    data.rows.length === 0
      ? `<tr><td colspan="${colCount}" style="padding:14px;text-align:center;color:#333;">ບໍ່ມີແຖວໃນຜົນທີ່ເລືອກໄວ້.</td></tr>`
      : data.rows
          .map((row) => {
            const tRaw = formatDateTime(row.submittedAt);
            const t = escapeHtmlForPrintUnsafeContext(tRaw);
            const cells = row.cells
              .map((cell) => `<td>${renderPrintCellHtml(cell)}</td>`)
              .join("");
            return `<tr><td>${t}</td>${cells}</tr>`;
          })
          .join("");

  const bodyHtml = `
    <div class="frp-header">
      ${logoBlock}
      ${brandRaw ? `<div class="frp-brand">${brand}</div>` : ""}
      <div class="frp-title">${title}</div>
      <div class="frp-meta">
        ພິມ: ${genAt}${filteredNote} · ຈຳນວນແຖວ: ${String(data.rows.length)}
      </div>
    </div>
    <table class="fr-table">
      <thead>${theadCells}</thead>
      <tbody>${tbody}</tbody>
    </table>
  `;

  const printWindow = window.open("", "_blank");
  if (!printWindow) return;

  const docTitleRaw = `${data.formTitle} — ຜົນຕອບ`;

  printWindow.document.write(`
    <!DOCTYPE html>
    <html lang="lo">
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtmlForPrintUnsafeContext(docTitleRaw)}</title>
        <style>${FORM_RESPONSES_PRINT_STYLES_A4}</style>
      </head>
      <body>${bodyHtml}</body>
    </html>
  `);
  printWindow.document.close();

  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 280);
}
